/**
 * Smooth product card hover.
 *
 * 1. Cross-fade. Horizon's card gallery ("Show second image on hover") swaps to the next
 *    photo by jumping its slideshow one slide in a single frame, so the change reads as a
 *    flicker. Before that swap, this lays a copy of the outgoing photo over the card; once
 *    the slideshow has moved underneath, the copy fades out, so the two photos cross-fade.
 *    Leaving the card fades back the same way. Mouse only (Horizon ignores touch here too)
 *    and off for reduced motion.
 *
 * 2. No product page download on hover. Horizon's quick add fetches and parses the whole
 *    product page every time the pointer enters a card (up to ~900 KB of HTML for cast
 *    iron, 60-100ms of parsing), then throws the result away. The page is sent with
 *    max-age=0, so nothing is cached either: the work only stalls the fade. Clicking quick
 *    add still fetches the page exactly as before.
 */
const FADE_MS = 250;
// Horizon moves the slideshow a little after the pointer event; give up if it never does
// (e.g. a card with one photo) so the copy never lingers.
const SWAP_TIMEOUT_MS = 500;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {WeakMap<HTMLElement, HTMLElement>} */
const overlays = new WeakMap();

/**
 * The slide currently in view: the one whose left edge is nearest the scroller's.
 * @param {HTMLElement} scroller
 */
function visibleSlide(scroller) {
  const left = scroller.getBoundingClientRect().left;
  let closest = null;
  let closestDistance = Infinity;

  for (const slide of scroller.children) {
    const distance = Math.abs(slide.getBoundingClientRect().left - left);
    if (distance < closestDistance) {
      closest = slide;
      closestDistance = distance;
    }
  }

  return closest;
}

/**
 * A plain copy of the photo, styled and placed exactly over the original.
 * @param {HTMLImageElement} image
 * @param {HTMLElement} gallery
 */
function createOverlay(image, gallery) {
  const imageStyle = getComputedStyle(image);
  const imageRect = image.getBoundingClientRect();
  const galleryRect = gallery.getBoundingClientRect();

  const overlay = document.createElement('div');
  overlay.className = 'cr-card-hover-fade';
  overlay.setAttribute('aria-hidden', 'true');
  Object.assign(overlay.style, {
    position: 'absolute',
    left: `${imageRect.left - galleryRect.left}px`,
    top: `${imageRect.top - galleryRect.top}px`,
    width: `${imageRect.width}px`,
    height: `${imageRect.height}px`,
    zIndex: '1',
    pointerEvents: 'none',
    overflow: 'hidden',
    borderRadius: imageStyle.borderRadius,
  });

  const copy = new Image();
  copy.alt = '';
  copy.decoding = 'sync';
  copy.src = image.currentSrc || image.src;
  Object.assign(copy.style, {
    display: 'block',
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    padding: imageStyle.padding,
    objectFit: imageStyle.objectFit,
    objectPosition: imageStyle.objectPosition,
    backgroundColor: imageStyle.backgroundColor,
  });

  overlay.append(copy);
  return overlay;
}

/** @param {HTMLElement} gallery */
function crossFade(gallery) {
  const scroller = gallery.querySelector('slideshow-slides');
  if (!(scroller instanceof HTMLElement)) return;

  const image = visibleSlide(scroller)?.querySelector('img');
  if (!(image instanceof HTMLImageElement) || !image.complete || image.naturalWidth === 0) return;

  overlays.get(gallery)?.remove();

  if (getComputedStyle(gallery).position === 'static') gallery.style.position = 'relative';

  const overlay = createOverlay(image, gallery);
  gallery.append(overlay);
  overlays.set(gallery, overlay);

  const startLeft = scroller.scrollLeft;
  const startTime = performance.now();

  const waitForSwap = () => {
    if (!overlay.isConnected) return;

    if (Math.abs(scroller.scrollLeft - startLeft) > 1) {
      overlay
        .animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, easing: 'ease-out', fill: 'forwards' })
        .finished.catch(() => {})
        .finally(() => overlay.remove());
      return;
    }

    if (performance.now() - startTime > SWAP_TIMEOUT_MS) {
      overlay.remove();
      return;
    }

    requestAnimationFrame(waitForSwap);
  };

  requestAnimationFrame(waitForSwap);
}

/** @param {PointerEvent} event */
function handlePointer(event) {
  if (event.pointerType !== 'mouse' || reducedMotion.matches) return;

  const gallery = event.target;
  if (!(gallery instanceof HTMLElement) || !gallery.hasAttribute('on:pointerenter')) return;
  if (!gallery.classList.contains('card-gallery')) return;

  crossFade(gallery);
}

// Horizon handles the card's pointer events from a capturing listener on the document, so
// this listens one step earlier, on the window: the outgoing photo is copied before the
// slideshow moves. pointerenter/leave do not bubble, but they do pass through capture.
window.addEventListener('pointerenter', handlePointer, { capture: true, passive: true });
window.addEventListener('pointerleave', handlePointer, { capture: true, passive: true });

// The card's own pointerenter only drives the quick add prefetch (product-card.js); the
// photo swap listens on the gallery, a separate element with its own event.
window.addEventListener(
  'pointerenter',
  (event) => {
    if (event.target instanceof HTMLElement && event.target.localName === 'product-card') {
      event.stopImmediatePropagation();
    }
  },
  { capture: true }
);
